// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, ViewChild } from '@angular/core';
import { HeaderService } from '../../header.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from 'src/services/configuration-service';
import {
  GridstackComponent,
  NgGridStackOptions,
  NgGridStackWidget,
} from 'gridstack/dist/angular';
import { GridStack, GridStackNode, GridStackOptions } from 'gridstack';
import {DashboardGridService} from './dashboard-grid.service';
import { EMPTY, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {UserService} from '../../../services/user-service';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {selectUser} from '../../store/user/user.selectors';
import {UntilDestroy} from '@ngneat/until-destroy';

let ids = 1;

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-dynamic-dashboard',
    templateUrl: './dynamic-dashboard.component.html',
    styleUrls: ['./dynamic-dashboard.component.scss'],
    standalone: false
})
export class DynamicDashboardComponent {
  @ViewChild("gridstackElement1") gridComp?: GridstackComponent;
  @ViewChild("palleteGridstack") palleteGrid?: GridstackComponent;

  // Changing this version will cause all dashboard localstorage to be removed for everyone.
  // This is meant as a temporary way to refresh the dashboard until we have a way to add/remove components.
  // TODO: REMOVE ME WHEN COMPONENTS CAN BE ADDED/REMOVED
  private dashboardVersion = "21";
  private uid: string = '';
  widgetListOpen = false;
  dragging = false;
  firstLoad = true;

  private gridStackOptions: GridStackOptions = {
    cellHeight: 43,
    column: 'auto', // size to match container. make sure to include gridstack-extra.min.css
    acceptWidgets: true, // true will accept .grid-stack-item by default or can use value '.grid-stack-item.sub'
    minRow: 1,
    handle: '.draggable',
    float: false,
    sizeToContent: true,
    margin: '10px 10px 10px 10px',
  };

  private subGridStackOptions: GridStackOptions = {
    cellHeight: 43,
    column: 'auto', // size to match container. make sure to include gridstack-extra.min.css
    acceptWidgets: true, // true will accept .grid-stack-item by default or can use value '.grid-stack-item.sub'
    minRow: 1,
    handle: '.draggable',
    float: false,
    sizeToContent: true,
    margin: '10px 10px 0 10px',
  };

  gridOptions$: Observable<{nestedGridOptions: NgGridStackOptions}> = this.store.select(selectUser()).pipe(
    tap(user => {
      this.uid = user?.id.toString() || '';
    }),
    switchMap(user => {
        return user ? this.userService.get_organization(user?.group_id) : EMPTY;
    }),
    map(this.getNestedGridOptions.bind(this))
  );
  palletGridOptions: NgGridStackOptions;

  constructor(private headerService: HeaderService,
              private readonly dashboardGridService: DashboardGridService,
              private readonly userService: UserService,
              private readonly store: Store<AppState>,
              private translate: TranslateService,
              private readonly configurationService: ConfigurationService) {
    this.headerService.setContextHelp(['help.dashboard']);
    this.headerService.setTitle(this.translate.instant('dashboard.title'));
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-view-dashboard');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);
    this.headerService.setOrgSelectorDefaultOption(this.translate.instant('SELECTOR_ALL_ORGANIZATIONS'));
  }

  public recursiveAddEventHandlers(grid: GridStack) {
    this.addEvents(grid);
    for (let node of grid.engine.nodes) {
      if ('subGrid' in node) {
        this.recursiveAddEventHandlers(node.subGrid!);
      }
    }
  }

  public recursiveAddPalletEventHandlers(grid: GridStack) {
    this.addPaletEvents(grid);
    for (let node of grid.engine.nodes) {
      if ('subGrid' in node) {
        this.recursiveAddEventHandlers(node.subGrid!);
      }
    }
  }

  private addEvents(grid: GridStack) {
    grid.on('change', (event: any, items: any) => {
      // This is for if an item within a nested grid is relocated or resized, you have to check if the grid is over-extended
      let itemOverextension = this.containerOverExtendedNumberOfRows(items);
      if (itemOverextension > 0) {
        this.extendItemRows(items[0].grid.parentGridItem, itemOverextension);
      }
      this.saveGrid();
    })
    .on('dragstart', () => this.dragging = true)
    .on('dragstop', () => this.dragging = false)
    .on('dropped', (event: any, previousNode: any, newNode: any) => {
      // This is if an item is moved into a nested grid
      let itemOverextension = this.itemOverExtendingContainerBy(newNode);
      if (itemOverextension > 0) {
        this.extendItemRows(newNode.grid!.parentGridItem!, itemOverextension);
      }
      this.saveGrid();
    })
    .on('resizestop', (event: any, el: any) => {
      // This is if an item which itself contains a nested grid is resized
      this.dashboardGridService.gridResize.next(event);
      let n = el.gridstackNode;
      if (n.subGrid) {
        let itemOverextension = this.containerOverExtendedNumberOfRows(n.subGrid.engine.nodes);
        if (itemOverextension > 0) {
          this.extendItemRows(n, itemOverextension);
        }
      }
      this.saveGrid();
    });
  }

  private addPaletEvents(grid: GridStack) {
    grid.on('change', (event: any, items: any) => {
      // This is for if an item within a nested grid is relocated or resized, you have to check if the grid is over-extended
      let itemOverextension = this.containerOverExtendedNumberOfRows(items);
      if (itemOverextension > 0) {
        this.extendItemRows(items[0].grid.parentGridItem, itemOverextension);
      }
      this.saveGrid();
    })
    .on('dropped', (event: any, previousNode: any, newNode: any) => {
      // This is if an item is moved into a nested grid
      let itemOverextension = this.itemOverExtendingContainerBy(newNode);
      if (itemOverextension > 0) {
        this.extendItemRows(newNode.grid!.parentGridItem!, itemOverextension);
      }
      this.saveGrid();
    })
    .on('resizestop', (event: any, el: any) => {
      // This is if an item which itself contains a nested grid is resized
      this.dashboardGridService.gridResize.next(event);
      let n = el.gridstackNode;
      if (n.subGrid) {
        let itemOverextension = this.containerOverExtendedNumberOfRows(n.subGrid.engine.nodes);
        if (itemOverextension > 0) {
          this.extendItemRows(n, itemOverextension);
        }
      }
      this.saveGrid();
    });
  }

  private extendItemRows(gridItem: GridStackNode, numberOfRows: number) {
    this.gridComp.grid!.update(gridItem.el!, {h: gridItem.h! + numberOfRows})
  }

  containerOverExtendedNumberOfRows(items: GridStackNode[]) {
    let extendAmount = 0;
    for (let item of items) {
      let itemOverextension = this.itemOverExtendingContainerBy(item);
      if (itemOverextension > extendAmount) {
        extendAmount = itemOverextension;
      }
    }
    return extendAmount;
  }

  itemOverExtendingContainerBy(item: GridStackNode) {
    if (!item.grid!.parentGridItem) {
      return 0;
    }
    return item.y! + item.h! - item.grid!.parentGridItem.h!;
  }

  public add() {
    // TODO: BUG the content doesn't appear until widget is moved around (or another created). Need to force
    // angular detection changes...
    this.gridComp?.grid?.addWidget({x: 3, y: 0, w: 2, content: `item ${ids}`, id: String(ids++)});
  }

  public delete() {
    let grid = this.gridComp?.grid;
    if (!grid) return;
    let node = grid.engine.nodes[0];
    // delete any children first before subGrid itself...
    if (node?.subGrid && node.subGrid.engine.nodes.length) {
      grid = node.subGrid;
      node = grid.engine.nodes[0];
    }
    if (node) grid.removeWidget(node.el!);
  }

  public saveGrid() {
    const serializedData = this.gridComp?.grid?.save(false, true) as GridStackOptions || ''; // no content, full options
    localStorage.setItem(`dashboard_${this.uid}`, JSON.stringify(serializedData));
    localStorage.setItem(`dashboardVersion_${this.uid}`, this.dashboardVersion);
    if (this.palleteGrid?.grid) {
        this.saveWidgetList();
    }
  }

  filterOutMapComponent(gridStackOptions: NgGridStackOptions) {
    if (gridStackOptions?.children) {
      gridStackOptions.children = gridStackOptions.children
        .filter(child => child.selector != 'app-endpoint-location')
        .map(child => {
          child.subGridOpts = this.filterOutMapComponent(child.subGridOpts);
          return child;
        });
    }
    return gridStackOptions;
  }

  showWidgetList() {
    const palletLayout = localStorage.getItem(`pallet_${this.uid}`);
    this.palletGridOptions = JSON.parse(palletLayout) || this.getDefaultPalletOptions();
    this.widgetListOpen = true;
    setTimeout(() => {
      if (this.palleteGrid?.grid) {
        this.recursiveAddPalletEventHandlers(this.palleteGrid.grid);
      }
    }, 0);
  }

  saveWidgetList() {
    const palletData = this.palleteGrid?.grid?.save(false, true) as GridStackOptions ||
      this.getDefaultPalletOptions(); // no content, full options
    localStorage.setItem(`pallet_${this.uid}`, JSON.stringify(palletData));
  }

  hideWidgetList() {
    this.saveWidgetList();
    this.widgetListOpen = false;
  }

  getDefaultPalletOptions() {
    return {
      ...this.gridStackOptions,
      children: []
    };
  }

  getNestedGridOptions(organization) {
    let showMap = false;

    if (!!organization) {
      showMap = !!organization.enable_endpoint_map;
    }

    const statsOverview: NgGridStackWidget[] = [
      {x: 0, y: 0, w: 3, h: 4, minW: 3, maxW: 3, minH: 4, maxH: 4, selector: 'app-endpoints-connected', noResize: true, id: 'endpoints-connected'},
      {x: 3, y: 0, w: 3, h: 4, minW: 3, maxW: 3, minH: 4, maxH: 4, selector: 'app-endpoints-total', noResize: true, id: 'endpoints-total'},
      {x: 6, y: 0, w: 3, h: 4, minW: 3, maxW: 3, minH: 4, maxH: 4, selector: 'app-endpoints-locked', noResize: true, id: 'endpoints-locked'},
      {x: 9, y: 0, w: 3, h: 4, minW: 3, maxW: 3, minH: 4, maxH: 4, selector: 'app-threats-detected', noResize: true, id: 'threats-detected'}
    ];

    let subChildren: NgGridStackWidget[];

    if (showMap) {
      subChildren = [
        {x: 0, y: 0, w: 12, h: 5, subGridOpts: {children: statsOverview, class: 'stats-component', ...this.subGridStackOptions}, id: 'statsOverview'},
        {x: 0, y: 5, w: 6, h: 9, minW: 2, selector: 'app-bitlocker-compliance', id: 'bitlocker-compliance'},
        {x: 6, y: 5, w: 6, h: 9, selector: 'app-endpoint-location', id: 'endpoint-location'},
        {x: 0, y: 14, w: 6, h: 9, selector: 'app-endpoint-activity', id: 'endpoint-activity'},
        {x: 6, y: 14, w: 6, h: 9, selector: 'app-decryption-activity', id: 'decryption-activity'},
        {x: 0, y: 23, w: 8, h: 7, selector: 'app-threat-activity-overview', id: 'threat-activity-overview'},
        {x: 8, y: 23, w: 4, h: 7, minW: 4, selector: 'app-top-threats', id: 'top-threats'},
        {x: 0, y: 30, w: 4, h: 7, minW: 4, selector: 'app-endpoints-overview', id: 'endpoints-overview'},
        {x: 4, y: 30, w: 4, h: 7, minW: 4, selector: 'app-endpoint-protections', id: 'endpoint-protections'},
        {x: 8, y: 30, w: 4, h: 7, minW: 4, selector: 'app-endpoint-top-threats', id: 'endpoint-top-threats'},
      ];
    } else {
      subChildren = [
        {x: 0, y: 0, w: 12, h: 5, subGridOpts: {children: statsOverview, class: 'stats-component', ...this.subGridStackOptions}, id: 'statsOverview'},
        {x: 0, y: 5, w: 6, h: 9, minW: 2, selector: 'app-bitlocker-compliance', id: 'bitlocker-compliance'},
        {x: 6, y: 5, w: 6, h: 9, selector: 'app-endpoint-activity', id: 'endpoint-activity'},
        {x: 0, y: 14, w: 6, h: 9, selector: 'app-decryption-activity', id: 'decryption-activity'},
        {x: 6, y: 14, w: 6, h: 9, selector: 'app-threat-activity-overview', id: 'threat-activity-overview'},
        {x: 0, y: 23, w: 4, h: 7, minW: 4, selector: 'app-top-threats', id: 'top-threats'},
        {x: 4, y: 23, w: 4, h: 7, minW: 4, selector: 'app-endpoints-overview', id: 'endpoints-overview'},
        {x: 8, y: 23, w: 4, h: 7, minW: 4, selector: 'app-endpoint-protections', id: 'endpoint-protections'},
        {x: 0, y: 30, w: 4, h: 7, minW: 4, selector: 'app-endpoint-top-threats', id: 'endpoint-top-threats'},
      ];
    }

    if (this.configurationService.showAgentMenus && this.configurationService.showAgentMenusExtended) {
      subChildren.push({x: 0, y: 37, w: 6, h: 9, minW: 2, selector: 'app-endpoints-attention', id: 'endpoints-attention'},
        {x: 6, y: 37, w: 6, h: 9, minW: 2, selector: 'app-cigent-compliance', id: 'cigent-compliance'},
        {x: 0, y: 46, w: 6, h: 9, minW: 2, selector: 'app-licensing-posture', id: 'licensing-posture'})
    }

    let nestedGridOptions: NgGridStackOptions = { // main grid options
      ...this.gridStackOptions,
      children: subChildren
    };
    this.palletGridOptions = {
      ...this.gridStackOptions,
      children: []
    };

    const dashboardVersion = localStorage.getItem(`dashboardVersion_${this.uid}`);
    if (!dashboardVersion || dashboardVersion != this.dashboardVersion) {
      localStorage.removeItem('dashboardVersion');  // Old variable
      localStorage.removeItem('dashboard');  // Old variable
      localStorage.removeItem('pallet');  // Old variable
      localStorage.removeItem(`dashboard_${this.uid}`);
      localStorage.removeItem(`pallet_${this.uid}`);
    }

    if (this.firstLoad) {
      const dashboardLayout = localStorage.getItem(`dashboard_${this.uid}`);
      const palletLayout = localStorage.getItem(`pallet_${this.uid}`);
      if (dashboardLayout) {
        nestedGridOptions = showMap ? JSON.parse(dashboardLayout) : this.filterOutMapComponent(JSON.parse(dashboardLayout));
      }
      if (palletLayout) {
        this.palletGridOptions = showMap ? JSON.parse(palletLayout) : this.filterOutMapComponent(JSON.parse(palletLayout));
      }
      this.firstLoad = false;
    }

    // load the dashboard layout from localStorage after initializing
    setTimeout(() => {
      if (this.gridComp?.grid) {
        this.recursiveAddEventHandlers(this.gridComp.grid);
        this.saveGrid();
      }
      if (this.palleteGrid?.grid) {
        this.recursiveAddPalletEventHandlers(this.palleteGrid.grid);
        this.saveGrid();
      }
    }, 0);

    return {nestedGridOptions};
  }

  arrange() {
    this.gridComp?.grid?.compact();
    this.palleteGrid?.grid?.compact();
  }

  restoreDefaults() {
    this.gridOptions$ = this.store.select(selectUser()).pipe(
      switchMap(user => this.userService.get_organization(user?.group_id)),
      map(this.getNestedGridOptions.bind(this))
    );
  }
}
